import { computed, inject, provide, ref, shallowRef, type ComputedRef, type InjectionKey, type Ref } from 'vue'

/** Патч одного поля-связи: финальный payload для PUT values + мета для оверлея строк. */
export type FieldLinkPatch = {
  fieldId: number
  payload: { field_id: number; value: string | null; value_list?: number[] | null }
  /** Новые строки (id + имя) для отображения в таблице до сохранения. */
  addedRows: { objectId: number; name: string }[]
  /** id строк, помеченных на отвязку (для зачёркивания). */
  removedIds: number[]
}

export type PendingFieldLinks = {
  patches: Readonly<Ref<Map<number, FieldLinkPatch>>>
  hasPending: ComputedRef<boolean>
  stage: (patch: FieldLinkPatch) => void
  undo: (fieldId: number) => void
  clear: () => void
  patchFor: (fieldId: number) => FieldLinkPatch | undefined
  /** Оверлей entries для объединения с `values` при сохранении. */
  buildValuesOverlay: () => { field_id: number; value: string | null; value_list?: number[] | null }[]
}

export const PENDING_FIELD_LINKS_KEY: InjectionKey<PendingFieldLinks> = Symbol('pendingFieldLinks')

/**
 * Очередь отложенных изменений полей-связей (нормализованный паспорт).
 * Каждое поле хранит финальный желаемый payload; применяется при «Сохранить».
 */
export function usePendingFieldLinks(): PendingFieldLinks {
  const patches = ref<Map<number, FieldLinkPatch>>(new Map())

  const hasPending = computed(() => patches.value.size > 0)

  function stage(patch: FieldLinkPatch): void {
    const next = new Map(patches.value)
    next.set(patch.fieldId, patch)
    patches.value = next
  }

  function undo(fieldId: number): void {
    const next = new Map(patches.value)
    next.delete(fieldId)
    patches.value = next
  }

  function clear(): void {
    patches.value = new Map()
  }

  function patchFor(fieldId: number): FieldLinkPatch | undefined {
    return patches.value.get(fieldId)
  }

  function buildValuesOverlay() {
    return [...patches.value.values()].map((p) => p.payload)
  }

  return { patches, hasPending, stage, undo, clear, patchFor, buildValuesOverlay }
}

const NOOP: PendingFieldLinks = {
  patches: shallowRef(new Map()),
  hasPending: computed(() => false),
  stage: () => {},
  undo: () => {},
  clear: () => {},
  patchFor: () => undefined,
  buildValuesOverlay: () => [],
}

/** inject с no-op fallback для контекстов без модели паспорта. */
export function usePendingFieldLinksOptional(): PendingFieldLinks {
  return inject(PENDING_FIELD_LINKS_KEY, NOOP)
}
